/**
 * MTA collection: grid / list view toggle.
 * Injects a small two-button toggle into Dwell's facets toolbar and adds
 * `.mta-list-view` to <results-list>. Choice persists in localStorage.
 * Re-injects after Dwell morphs the section (filter / sort / pagination).
 */
(() => {
  const KEY = 'mta-collection-view';
  const LIST = 'mta-list-view';

  const read = () => {
    try {
      return localStorage.getItem(KEY) === 'list' ? 'list' : 'grid';
    } catch (e) {
      return 'grid';
    }
  };

  const write = (value) => {
    try {
      localStorage.setItem(KEY, value);
    } catch (e) {
      /* private mode / blocked storage: ignore */
    }
  };

  const ICON_GRID =
    '<svg viewBox="0 0 16 16" width="16" height="16" aria-hidden="true" fill="none" stroke="currentColor" stroke-width="1.5"><rect x="1.5" y="1.5" width="5" height="5" rx="1"/><rect x="9.5" y="1.5" width="5" height="5" rx="1"/><rect x="1.5" y="9.5" width="5" height="5" rx="1"/><rect x="9.5" y="9.5" width="5" height="5" rx="1"/></svg>';
  const ICON_LIST =
    '<svg viewBox="0 0 16 16" width="16" height="16" aria-hidden="true" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"><path d="M2 3.5h12M2 8h12M2 12.5h12"/></svg>';

  const apply = (view) => {
    document.querySelectorAll('results-list').forEach((list) => {
      list.classList.toggle(LIST, view === 'list');
    });
    document.querySelectorAll('.mta-view-toggle__btn').forEach((btn) => {
      const active = btn.dataset.view === view;
      btn.setAttribute('aria-pressed', active ? 'true' : 'false');
    });
  };

  const buildToggle = () => {
    const wrap = document.createElement('div');
    wrap.className = 'mta-view-toggle';
    wrap.setAttribute('role', 'group');
    wrap.setAttribute('aria-label', 'Layout');
    wrap.setAttribute('data-skip-node-update', 'true');
    wrap.innerHTML =
      `<button type="button" class="mta-view-toggle__btn" data-view="grid" aria-label="Grid view" aria-pressed="false">${ICON_GRID}</button>` +
      `<button type="button" class="mta-view-toggle__btn" data-view="list" aria-label="List view" aria-pressed="false">${ICON_LIST}</button>`;
    wrap.addEventListener('click', (event) => {
      const btn = event.target.closest('.mta-view-toggle__btn');
      if (!btn) return;
      const view = btn.dataset.view;
      write(view);
      apply(view);
    });
    return wrap;
  };

  const ICON_SORT =
    '<svg viewBox="0 0 16 16" width="16" height="16" aria-hidden="true" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"><path d="M2 4h12M4 8h8M6 12h4"/></svg>';

  /**
   * Mobile toolbar (< 750px). Dwell's `.facets-toggle` only renders the
   * "Filter" button and moves sorting into the filters drawer. We add:
   *   - the product count next to "Filter" (text mirrored from the desktop
   *     `.products-count-wrapper`, which is display:none on mobile),
   *   - a "Sort" button that opens the same drawer and expands its sort panel
   *     (sort stays reachable in the drawer too),
   *   - the grid / list toggle.
   * Compact row: Filter · count | Sort · toggle.
   */
  const injectMobile = (list) => {
    const toggle = list.querySelector('.facets-toggle');
    if (!toggle) return;

    const wrapper = toggle.querySelector('.facets-toggle__wrapper');
    const countSource = list.querySelector('.products-count-wrapper');
    if (wrapper && countSource && !wrapper.querySelector('.mta-mobile-count')) {
      const count = document.createElement('span');
      count.className = 'mta-mobile-count';
      count.setAttribute('data-skip-node-update', 'true');
      count.textContent = countSource.textContent.trim();
      wrapper.appendChild(count);
    } else if (wrapper && countSource) {
      const count = wrapper.querySelector('.mta-mobile-count');
      const text = countSource.textContent.trim();
      if (count && count.textContent !== text) count.textContent = text;
    }

    let controls = toggle.querySelector('.facets-mobile-wrapper');
    if (!controls) {
      controls = document.createElement('div');
      controls.className = 'facets-mobile-wrapper facets-controls-wrapper';
      toggle.appendChild(controls);
    }

    const hasDrawer = !!document.getElementById('filters-drawer');
    const sortInDrawer = document.querySelector('#filters-drawer .sorting-filter');
    if (hasDrawer && sortInDrawer && !controls.querySelector('.mta-mobile-sort')) {
      const sort = document.createElement('button');
      sort.type = 'button';
      sort.className = 'button button-unstyled button-unstyled--with-icon mta-mobile-sort';
      sort.setAttribute('on:click', '#filters-drawer/open');
      sort.setAttribute('aria-haspopup', 'dialog');
      sort.setAttribute('data-skip-node-update', 'true');
      sort.innerHTML = `<span class="svg-wrapper">${ICON_SORT}</span>Sort`;
      sort.addEventListener('click', () => {
        // Drawer opens on the next frame (dialog.js); then expand the sort panel.
        setTimeout(() => {
          const panel = document.querySelector('#filters-drawer .sorting-filter details');
          if (panel instanceof HTMLDetailsElement) {
            panel.open = true;
            panel.scrollIntoView({ block: 'nearest' });
          }
          const select = document.querySelector('#filters-drawer .sorting-filter select:not([disabled])');
          if (select instanceof HTMLSelectElement && select.offsetParent) select.focus();
        }, 120);
      });
      controls.insertBefore(sort, controls.firstChild);
    }

    if (!controls.querySelector('.mta-view-toggle')) {
      controls.appendChild(buildToggle());
    }
  };

  const inject = () => {
    document.querySelectorAll('results-list').forEach((list) => {
      const hosts = list.querySelectorAll('.facets-controls-wrapper:not(.facets-mobile-wrapper)');
      hosts.forEach((host) => {
        if (host.querySelector('.mta-view-toggle')) return;
        host.appendChild(buildToggle());
      });
      injectMobile(list);
    });
    apply(read());
  };

  let scheduled = false;
  const schedule = () => {
    if (scheduled) return;
    scheduled = true;
    requestAnimationFrame(() => {
      scheduled = false;
      inject();
    });
  };

  const start = () => {
    inject();
    // Dwell re-renders the results via section-renderer + morph; keep the toggle alive.
    const observer = new MutationObserver((mutations) => {
      for (const m of mutations) {
        if (m.type === 'childList' && (m.addedNodes.length || m.removedNodes.length)) {
          schedule();
          return;
        }
      }
    });
    document.querySelectorAll('results-list').forEach((list) => {
      observer.observe(list, { childList: true, subtree: true });
    });
    // Keep multiple tabs in sync.
    window.addEventListener('storage', (e) => {
      if (e.key === KEY) apply(read());
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
})();
